import { Component, OnInit, Input, SimpleChanges, EventEmitter, Output, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Location } from '@angular/common';
// services
import { MessageService } from "../../../../../../services/message.service";
import { MasterService } from "../../../../../../services/master.service";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import * as languageLibrary from '../../../../../../services/language'
import { StoreService } from "src/app/services/store.service";
import { PAYMENT_CYCLES } from '../../../../../../constants/payments'
import { formatCurrency, formatDecimal } from '../../../../../../utils/format'
import { clean } from "src/app/utils/numeric";
import { minValueValidator } from "src/app/utils/min-value.validator";
import { Sale } from "src/app/models/sale.model";
import { InsurancePolicy } from "src/app/models/insurance-policy.model";
import { Contact } from "src/app/models/contact.model";
import { ago } from "src/app/utils/date";
import moment from 'moment'

@Component({
    selector: "cpi",
    templateUrl: "./cpi.component.html",
    styleUrls: ["./cpi.component.scss"],
    standalone: false
})
export class CPIComponent implements OnInit, OnDestroy {

    @Input() saleId: number;

    @Input() sale: Sale;

    // Unique internal identity of the lot whose down payments will be queried for.
    @Input() lotId: number = 0

    @Input() cpiPolicyInfo: InsurancePolicy;

    @Output() emitterInformation$: EventEmitter<Object> = new EventEmitter()

protected status: string = "";

    protected paymentCycles = PAYMENT_CYCLES

    protected language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    protected sales: Sale[] = [];

    protected companiesCPI: Contact[] = [];

    protected words = languageLibrary.language

    protected idForAction: number = 0;

    //
    protected payments: Array<any> = []

    //
    protected records: number = 0

    //
    protected page: number = 1

    // Indicates whether the data is currently loading.
    protected loading: boolean = false

    // Indicates whether any more data is available
    protected exhausted: boolean = false

    protected cpiDetailToCopy: InsurancePolicy;

    protected calculating: boolean;

    public information = new FormGroup({
        id: new FormControl<number>(null),
        contactId: new FormControl<number>(null, [Validators.required]),
        buyerId: new FormControl<number>(null, [Validators.required]),
        saleId: new FormControl<number>(null, [Validators.required]),
        cpi: new FormControl<boolean>(true, [Validators.required]),
        policyNumber: new FormControl<string | null>(null, [Validators.required]),
        endDate: new FormControl<Date>(null, []),
        effectiveDate: new FormControl<string>(new Date().toISOString().substring(0, 10), [Validators.required]),
        premiumPerPayment: new FormControl<string>(formatCurrency(0), [minValueValidator(1)]),
        monthlyPremium: new FormControl<string>(formatCurrency(0), [Validators.required]),
        defaultDeductibleAmount: new FormControl<string>(formatCurrency(0), [Validators.required, minValueValidator(1)]),
        saleLoanBalance: new FormControl<string>(formatCurrency(0), [Validators.required, minValueValidator(1)]),
        months: new FormControl<number>(0, [Validators.required, Validators.min(1)]),
        firstScheduledPmtDue: new FormControl<string>(new Date().toISOString().substring(0, 10), [Validators.required]),
        paymentCycle: new FormControl<string>("Monthly", [Validators.required]),
        status: new FormControl<string>('active', [Validators.required]),
        companyName: new FormControl<string | null>(null, [])
    })

    get policyInEffect (): boolean {
        const id = this.information.get('id').value
        if (!id || isNaN(id) || (id <= 0)) {
            return false
        }

        return true
    }

    private subscriptions: Subscription[] = []

    constructor(private route: ActivatedRoute, private ms: MessageService, private master: MasterService, private store: StoreService, private location: Location) {}

    ngOnInit() {
        this.information.get('premiumPerPayment').disable()
        this.information.get('saleLoanBalance').disable()
        this.information.get('endDate').disable()

        this.fetch()

        this.subscriptions.push(
            this.information.get('contactId').valueChanges.subscribe((vendorId) => {
                this.fetchVendorDetail(vendorId);
            })
        )
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        if (changes.sale) {
            if (!this.sale) {
                return
            }
            
            this.information.patchValue({
                saleId: this.saleId,
                buyerId: this.sale['contactId'],
                effectiveDate: this.sale['saleDate'] instanceof Date ? this.sale['saleDate'].toISOString().substring(0, 10) : this.sale['saleDate'],
                firstScheduledPmtDue: this.sale['first'] instanceof Date ? this.sale['first'].toISOString().substring(0, 10) : this.sale['first'],
                paymentCycle: this.sale['paymentCycle'],
                saleLoanBalance: formatCurrency(this.sale['payoff']),
                premiumPerPayment: formatCurrency(this.sale['payment'])
            })
        }

        if (changes.cpiPolicyInfo) {
            if (!changes.cpiPolicyInfo.currentValue) {
                return
            }

            this.setInformation(this.cpiPolicyInfo)
        }
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }

    // todo: listen to output of navbar
    public listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break;
            default:
                break;
        }
    }

    public dateCheck() {
        if (!this.sale) {
            return
        }

        const firstScheduledPmtDueDate = this.information.get('firstScheduledPmtDue')
        const effDate = this.information.get('effectiveDate')
        const saleDate = this.sale['saleDate'] instanceof Date ? this.sale['saleDate'].toISOString().substring(0, 10) : this.sale['saleDate']
        
        if (effDate.value !== '') {
            if (effDate.value < saleDate) {
                this.information.controls['effectiveDate'].setErrors({ 'error': "Date from should be on or after the sale date" });
            }
        }

        if (firstScheduledPmtDueDate.value !== '') {
            if (firstScheduledPmtDueDate.value < saleDate) {
                this.information.controls['firstScheduledPmtDue'].setErrors({ 'error': "Date from should be on or after the sale date" });
            }
        }
    }

    protected async save() {
        if (!this.information.valid) {
            return
        }

        this.information.updateValueAndValidity({ onlySelf: true })

        const id = this.information.get('id').value
        if (isNaN(id) || (id <= 0)) {
            this.create()
            return
        }

        this.modify(id)
    }

    private modify(id: number) {
        if (!this.saleId || (this.saleId <= 0)) {
            return
        }

        const payload = JSON.parse(JSON.stringify(this.information.value))
        payload.premiumPerPayment = clean(payload.premiumPerPayment)
        payload.monthlyPremium = clean(payload.monthlyPremium)
        payload.saleLoanBalance = clean(payload.saleLoanBalance)
        payload.defaultDeductibleAmount = clean(payload.defaultDeductibleAmount)
        payload.endDate = moment(payload.firstScheduledPmtDue).add(payload.months, 'M')
        delete(payload.id)
        delete(payload.saleLoanBalance)
        delete(payload.premiumPerPayment)
        delete(payload.companyName)

        this.master.put(`sales/${this.saleId}/insurance/${id}`, { information: payload }, res => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.emitterInformation$.emit({ message: 'getAutoInsurance' })
        })
    }

    private create() {
        if (!this.saleId || (this.saleId <= 0)) {
            return
        }

        const payload = JSON.parse(JSON.stringify(this.information.value))
        payload.premiumPerPayment = clean(payload.premiumPerPayment)
        payload.monthlyPremium = clean(payload.monthlyPremium)
        payload.saleLoanBalance = clean(payload.saleLoanBalance)
        payload.defaultDeductibleAmount = clean(payload.defaultDeductibleAmount)
        payload.endDate = moment(payload.firstScheduledPmtDue).add(payload.months, 'M')
        delete(payload.id)
        delete(payload.saleLoanBalance)
        delete(payload.premiumPerPayment)
        delete(payload.companyName)

        if (payload.status.toLocaleLowerCase() !== 'inactive') {
            this.information.get('status').setValue('active', {emitEvent: false})
            payload.status = 'active'
        }

        this.master.post(`sales/${this.saleId}/insurance`, { information: payload }, res => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.emitterInformation$.emit({ message: 'getAutoInsurance' })
            this.information.get('id').setValue(parseInt(res.data.insurance))
            this.location.replaceState(`sales/${this.saleId}/insurance`);
            this.reset()
            this.fetchPayments()
        })
    }

    public setDetails(cpiDetail) {
        this.cpiDetailToCopy = cpiDetail
    }

    protected deactivate() {
        if (!this.saleId || (this.saleId <= 0)) {
            return
        }

        const id = this.information.get('id').value
        if (isNaN(id) || (id <= 0)) {
            return
        }

        this.master.patch(`sales/${this.saleId}/insurance/${id}/deactivate`, { saleId: this.saleId }, res => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.resetInformation()

            document.getElementById("btn-close-modal-cancel").click();
            this.emitterInformation$.emit({ message: 'getAutoInsurance' })
        })
    }

    protected setId(id) {
        this.idForAction = id
    }

    protected fetch() {
        if (!this.saleId || (this.saleId <= 0)) {
            return
        }

        this.master.get(`sales/${this.saleId}/insurance`, res => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (res.data.activePolicies.length <= 0) {
                return
            }

            if (!res.data.activePolicies[0].cpi) { // outside insurance?
                return
            }

            this.reset()
            this.fetchPayments()
        })
    }

    private setInformation(cpiPolicy: InsurancePolicy) {
        this.status = cpiPolicy['status']
        this.information.patchValue({
            id: cpiPolicy.id,
            cpi: cpiPolicy.cpi,
            saleId: cpiPolicy.saleId,
            effectiveDate: cpiPolicy.effectiveDate instanceof Date ? cpiPolicy.effectiveDate.toISOString().substring(0, 10) : cpiPolicy.effectiveDate,
            premiumPerPayment: formatCurrency(cpiPolicy.premiumPerPayment),
            monthlyPremium: formatCurrency(cpiPolicy.monthlyPremium),
            defaultDeductibleAmount: formatCurrency(cpiPolicy.defaultDeductibleAmount),
            saleLoanBalance: formatCurrency(cpiPolicy.saleLoanBalance),
            months: cpiPolicy.months,
            firstScheduledPmtDue: cpiPolicy.firstScheduledPmtDue instanceof Date ? cpiPolicy.firstScheduledPmtDue.toISOString().substring(0, 10) : cpiPolicy.firstScheduledPmtDue,
            paymentCycle: cpiPolicy.paymentCycle,
            status: cpiPolicy.status,
            contactId: cpiPolicy.contactId,
            endDate: cpiPolicy.endDate,
            policyNumber: cpiPolicy.policyNumber,
            companyName: cpiPolicy.contact.companyName
        }, {emitEvent: false})

        if (this.status.toLocaleLowerCase() === 'active') {
            this.information.disable()
        }
    }

    protected fetchVendors() {
        this.master.get(`contacts?type=cpiVendor`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.companiesCPI = res.data.contacts
        })
    }

    public setContactIdEvent(contactId: number) {
        if (!contactId || (contactId <= 0)) {
            return
        }

        this.information.get("contactId").setValue(contactId)
    }

    protected fetchVendorDetail(vendorId: number) {
        if (!vendorId || (vendorId <= 0)) {
            return
        }

        this.master.get(`contacts/${vendorId}/cpi`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.contactCPI) {
                return
            }

            this.information.get('defaultDeductibleAmount').setValue(formatCurrency(res.data.contactCPI.defaultDeductible))
            this.information.get('monthlyPremium').setValue(formatCurrency(res.data.contactCPI.defaultPremium))
            this.information.get('policyNumber').setValue(res.data.contactCPI.policyNumber)
        })
    }

    private resetInformation() {
        this.status = ''
        this.idForAction = null
        this.information.reset()
        this.information.patchValue({
            saleId : this.saleId,
            status: "Active",
            cpi: true,
            firstScheduledPmtDue: new Date().toISOString().substring(0, 10),
            effectiveDate: new Date().toISOString().substring(0, 10),
        })

        this.payments = []
    }

    protected resetSaleInformation() {
        this.saleId = null
        this.sale = null
        this.information.reset()
    }

    private reset = (): void => {
        this.page = 1
        this.exhausted = false
        this.records = 0
    }

    private fetchPayments = (): void => {
        if (this.loading) {
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        const id = this.information.get('id').value
        if (isNaN(id) || (id <= 0)) {
            return
        }

        let saleParam = ''
        if (this.saleId && (this.saleId > 0)) {
            saleParam = `&saleId=${this.saleId}`
        }

        let lotParam = ''
        if (this.lotId && (this.lotId > 0)) {
            lotParam = `&lot=${this.lotId}`
        }

        this.loading = true
        this.master.get(`payments/scheduled?page=${this.page}&sort=due|ASC${saleParam}&insurance=${id}&categories=cpi&statuses=unpaid&ipp=7`, (res) => {
            this.loading = false

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.records = res.data.records
            this.exhausted = (res.data.next === null)

            if (this.page > 1) {
                this.payments = this.payments.concat(res.data.payments)
                return
            }

            this.payments = res.data.payments
        })
    }

    /**
     * Generate a color class (past, today, etc..) for the given due date.
     * @param due A date to generate a color class by.
     */
    public dueToClass = (due: string): string => {
        return ago(due)
    }
}
